import { useEffect } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import {
  RHYTHM_EVENT_TAB_ID,
  DEFAULT_CONTROL_MODAL_VALUE,
  selectControlModal,
  selectEventNews,
  useReportStore,
} from '@/entities/report';

/**
 * Drives the Add New Event button of the ECG Events tab. Subscribes to the new-event list for its count, opens the add-event modal from the default control-modal values only when it is not already showing, and registers a window keydown listener firing the same action on W while the pointer hovers the rhythm-event tab zone.
 *
 * @returns An object with eventLength, onClickAddNewEvent and setControlModal.
 */
const useButtonAddNewEvent = () => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Replaces the modal-visibility flags of the ECG Events tab. Forwards the value to the store's setControlModal action with the TAB_2 key prepended.
   *
   * @param v - The new control-modal flags, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  /**
   * Reads the modal-visibility flags of the ECG Events tab. Applies the selectControlModal selector bound to the TAB_2 key to a one-off report-store snapshot rather than subscribing.
   *
   * @returns The current control-modal flags.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());

  const eventLength = eventNews.length;

  /**
   * Opens the add-new-event modal on the ECG Events tab. Stops default handling and propagation of the originating DOM event when one is given, and writes the modal state only if the add-event modal is not already showing.
   *
   * @param e - The originating click or keyboard event; optional, since the keyboard shortcut calls without one.
   * @returns Nothing.
   */
  const onClickAddNewEvent = (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!getControlModal().isShowAddNewEvent) {
      setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowAddNewEvent: true });
    }
  };

  useEffect(() => {
    /**
     * Window keydown handler that opens the add-new-event modal from the keyboard. Ignores auto-repeat, presses made while the pointer is outside the rhythm-event tab zone and any combination held with ctrl or meta, then treats keyCode 87 (W) as a click on Add New Event.
     *
     * @param event - The keydown event, read for repeat, keyCode and the ctrl and meta modifiers.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const tabZone = document.getElementById(RHYTHM_EVENT_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      if (keyCode === 87) {
        // W
        onClickAddNewEvent();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return {
    eventLength,
    onClickAddNewEvent,
    setControlModal,
  };
};

export default useButtonAddNewEvent;
